import { useAuthStore } from '@/features/auth/auth.store';

/**
 * Cliente HTTP da API.
 *
 * Renova o access token ao receber 401 e repete a chamada. A garantia de que
 * duas requisições simultâneas não disparam duas rotações mora no store —
 * `refresh()` compartilha a promessa em curso.
 */

const BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Rota pública: não envia token nem tenta renovar. */
  anonymous?: boolean;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { anonymous, body, headers, ...rest } = options;

  const send = async (): Promise<Response> => {
    const token = anonymous ? null : useAuthStore.getState().accessToken;
    return fetch(`${BASE}${path}`, {
      ...rest,
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  };

  let response = await send();

  if (response.status === 401 && !anonymous) {
    const renewed = await useAuthStore.getState().refresh();
    if (renewed) response = await send();
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const detail = payload as { message?: string; requestId?: string } | null;
    throw new ApiError(
      response.status,
      detail?.message ?? `Falha na requisição (${response.status})`,
      detail?.requestId,
    );
  }

  return payload as T;
}

export const get = <T>(path: string, options?: RequestOptions) =>
  api<T>(path, { ...options, method: 'GET' });

export const post = <T>(path: string, body?: unknown, options?: RequestOptions) =>
  api<T>(path, { ...options, method: 'POST', body });
